(function () {
  'use strict';

  if (window.AimiLeadCapture && window.AimiLeadCapture.loaded) return;

  var CLOSE_DELAY = 240;
  var SUCCESS_DELAY = 3800;
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var FIELD_ERRORS = {
    name: 'Enter your name.',
    email: 'Enter a valid email.',
    phone: 'Enter your phone number.',
    shoe_style: 'Enter a shoe style.',
    size: 'Select an EU size.'
  };

  function closestLeadCaptureForTrigger(trigger) {
    var direct = trigger.closest('aimi-lead-capture, aimi-custom-quote');
    if (direct) return direct;

    var productId = trigger.getAttribute('data-aimi-lead-capture-product-id');
    if (productId) {
      var productMatch = document.querySelector('aimi-lead-capture[data-product-id="' + cssEscape(productId) + '"]');
      if (productMatch) return productMatch;
    }

    var section = trigger.closest('.shopify-section');
    if (section) {
      var sectionMatch = section.querySelector('aimi-lead-capture, aimi-custom-quote');
      if (sectionMatch) return sectionMatch;
    }

    return document.querySelector('aimi-lead-capture, aimi-custom-quote');
  }

  function isVisibleField(field) {
    if (!(field instanceof HTMLElement)) return false;
    if (field.type === 'hidden') return false;
    return field.offsetParent !== null || field === document.activeElement;
  }

  function fieldValue(field) {
    if (!field || !('value' in field)) return '';
    return String(field.value || '').trim();
  }

  function formatEuSize(value) {
    if (!value) return '';
    var match = String(value).match(/(?:EU\s*)?([3-4][0-9])/i);
    if (!match) return '';
    var numeric = Number(match[1]);
    if (numeric < 35 || numeric > 42) return '';
    return 'EU ' + numeric;
  }

  function sanitizeTag(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/"/g, '\\"');
  }

  function normalizeAbsoluteUrl(path, variantId) {
    var url;

    try {
      url = new URL(path || window.location.pathname, window.location.origin);
    } catch (error) {
      url = new URL(window.location.pathname, window.location.origin);
    }

    if (variantId) {
      url.searchParams.set('variant', variantId);
    }

    return url.href;
  }

  class AimiLeadCaptureElement extends HTMLElement {
    connectedCallback() {
      if (this.__aimiLeadCaptureReady) return;

      this.__aimiLeadCaptureReady = true;
      this.productData = this.readProductData();
      this.selectedVariantId = String(this.dataset.selectedVariantId || this.productData.selectedVariantId || '');
      this.active = null;
      this.abortController = new AbortController();
      this.closeTimer = null;
      this.successTimer = null;
      this.hadScrollLock = false;
      this.isScrollLocked = false;

      var signal = this.abortController.signal;
      var scope = this.closest('.shopify-section') || this.closest('featured-product-information') || document;

      this.handleVariantEvent = this.handleVariantEvent.bind(this);
      scope.addEventListener('variant:change', this.handleVariantEvent, { signal: signal });
      scope.addEventListener('variant:update', this.handleVariantEvent, { signal: signal });
      scope.addEventListener('product:variant-change', this.handleVariantEvent, { signal: signal });
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.clearTimers();
      this.unlockScroll();
      this.removeActiveDialog();
      this.__aimiLeadCaptureReady = false;
    }

    readProductData() {
      var script = this.querySelector('[data-aimi-lead-capture-product-json]');
      if (!script) {
        return {
          product: {
            id: this.dataset.productId || '',
            title: this.dataset.productTitle || '',
            handle: this.dataset.productHandle || '',
            url: this.dataset.productUrl || window.location.pathname,
            type: ''
          },
          variants: [],
          selectedVariantId: this.dataset.selectedVariantId || '',
          colorOptionPosition: Number(this.dataset.colorOptionPosition || 0),
          sizeOptionPosition: Number(this.dataset.sizeOptionPosition || 0),
          sizeMode: this.dataset.sizeMode || 'footwear'
        };
      }

      try {
        return JSON.parse(script.textContent || '{}');
      } catch (error) {
        return {
          product: {},
          variants: [],
          selectedVariantId: this.dataset.selectedVariantId || '',
          colorOptionPosition: Number(this.dataset.colorOptionPosition || 0),
          sizeOptionPosition: Number(this.dataset.sizeOptionPosition || 0),
          sizeMode: this.dataset.sizeMode || 'footwear'
        };
      }
    }

    handleVariantEvent(event) {
      var detail = event.detail || {};
      var resource = detail.resource || detail.variant || {};
      var variantId = detail.variantId || detail.id || resource.id || '';

      if (variantId) {
        this.selectedVariantId = String(variantId);
        this.dataset.selectedVariantId = String(variantId);
      }
    }

    open(mode, trigger) {
      var requestedMode = mode === 'stock' ? 'stock' : 'quote';
      var template = this.querySelector('template[data-aimi-lead-template="' + requestedMode + '"]');
      if (!template) return;

      this.clearTimers();
      this.removeActiveDialog();

      var fragment = template.content.cloneNode(true);
      var dialog = fragment.querySelector('[data-aimi-lead-dialog]');
      if (!dialog) return;

      document.body.appendChild(fragment);

      this.active = {
        mode: requestedMode,
        dialog: dialog,
        form: dialog.querySelector('.aimi-lead-modal__contact-form'),
        customerForm: dialog.querySelector('.aimi-lead-modal__customer-form'),
        submitButton: dialog.querySelector('[data-aimi-lead-submit]'),
        closeButton: dialog.querySelector('[data-aimi-lead-close]'),
        successState: dialog.querySelector('[data-aimi-lead-success]'),
        systemError: dialog.querySelector('[data-aimi-lead-system-error]'),
        bodyField: dialog.querySelector('[data-aimi-lead-body]'),
        lastActiveElement: trigger instanceof HTMLElement ? trigger : document.activeElement,
        isSubmitting: false,
        abortController: new AbortController()
      };

      this.bindActiveDialog();
      this.updateDialogContext();
      this.resetActiveState(false);
      this.lockScroll();

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      window.requestAnimationFrame(function () {
        this.focusInitialField();
      }.bind(this));
    }

    bindActiveDialog() {
      if (!this.active) return;

      var signal = this.active.abortController.signal;
      var dialog = this.active.dialog;

      this.handleCloseClick = this.close.bind(this);
      this.handleDialogClick = this.onDialogClick.bind(this);
      this.handleDialogCancel = this.onDialogCancel.bind(this);
      this.handleDialogKeydown = this.onDialogKeydown.bind(this);
      this.handleSubmit = this.onSubmit.bind(this);
      this.handleFieldInput = this.onFieldInput.bind(this);

      if (this.active.closeButton) {
        this.active.closeButton.addEventListener('click', this.handleCloseClick, { signal: signal });
      }

      dialog.addEventListener('click', this.handleDialogClick, { signal: signal });
      dialog.addEventListener('cancel', this.handleDialogCancel, { signal: signal });
      dialog.addEventListener('keydown', this.handleDialogKeydown, { signal: signal });

      if (this.active.form) {
        this.active.form.addEventListener('submit', this.handleSubmit, { signal: signal });
        this.active.form.querySelectorAll('[data-aimi-lead-field]').forEach(function (field) {
          field.addEventListener('input', this.handleFieldInput, { signal: signal });
          field.addEventListener('change', this.handleFieldInput, { signal: signal });
        }, this);
      }
    }

    onDialogClick(event) {
      if (this.active && event.target === this.active.dialog) {
        this.close();
      }
    }

    onDialogCancel(event) {
      event.preventDefault();
      this.close();
    }

    onDialogKeydown(event) {
      if (!this.active) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      var focusable = this.getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.active.dialog.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onFieldInput(event) {
      if (!this.active) return;
      var field = event.target;
      if (!(field instanceof HTMLElement)) return;

      var key = field.getAttribute('data-aimi-lead-field');
      if (!key) return;

      if (key === 'reference_file') {
        this.syncReferenceFileName(field);
      }

      this.setFieldError(field, '');
      this.hideSystemError();
    }

    async onSubmit(event) {
      event.preventDefault();

      if (!this.active || !this.active.form || this.active.isSubmitting) return;

      this.hideSystemError();
      this.syncReferenceFileName();
      this.updateDialogContext();

      var firstInvalid = this.validateForm(this.active.form);
      if (firstInvalid) {
        firstInvalid.focus();
        return;
      }

      this.updateBodyField();
      this.prepareCustomerForm();
      this.setSubmitting(true);

      try {
        await this.submitForm(this.active.form);

        if (this.active.customerForm) {
          this.submitForm(this.active.customerForm).catch(function () {});
        }

        this.showSuccess();
      } catch (error) {
        this.showSystemError();
      } finally {
        this.setSubmitting(false);
      }
    }

    submitForm(form) {
      return fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html'
        }
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('Lead capture request failed.');
        }

        return response.text();
      });
    }

    validateForm(form) {
      var firstInvalid = null;
      var fields = Array.prototype.slice.call(form.querySelectorAll('[data-aimi-lead-field]'));

      fields.forEach(function (field) {
        if (!isVisibleField(field)) return;

        var key = field.getAttribute('data-aimi-lead-field') || '';
        var value = fieldValue(field);
        var message = '';

        if (field.hasAttribute('required') && !value) {
          message = FIELD_ERRORS[key] || 'Complete this field.';
        } else if (key === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          message = FIELD_ERRORS.email;
        } else if (key === 'name' && value && value.length < 2) {
          message = 'Name must be at least 2 characters.';
        }

        this.setFieldError(field, message);

        if (message && !firstInvalid) {
          firstInvalid = field;
        }
      }, this);

      return firstInvalid;
    }

    setFieldError(field, message) {
      if (!field || !this.active || !this.active.form) return;

      var key = field.getAttribute('data-aimi-lead-field');
      if (!key) return;

      if (message) {
        field.setAttribute('aria-invalid', 'true');
      } else {
        field.removeAttribute('aria-invalid');
      }

      var error = this.active.form.querySelector('[data-aimi-lead-error="' + key + '"]');
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
    }

    syncReferenceFileName(field) {
      if (!this.active || !this.active.form) return;

      var fileField = field || this.active.form.querySelector('[data-aimi-lead-field="reference_file"]');
      var hiddenField = this.active.form.querySelector('[data-aimi-lead-field="reference_image"]');
      if (!fileField || !hiddenField) return;

      var files = fileField.files ? Array.prototype.slice.call(fileField.files) : [];
      hiddenField.value = files.map(function (file) {
        return file.name;
      }).join(', ');
    }

    updateDialogContext() {
      if (!this.active || !this.active.form) return;

      var context = this.getContext();
      var form = this.active.form;
      var hiddenFields = {
        product: context.productTitle,
        variant: context.variantTitle,
        variant_id: context.variantId,
        handle: context.handle,
        product_url: context.productUrl,
        selected_color: context.selectedColor,
        timestamp: context.timestamp
      };

      Object.keys(hiddenFields).forEach(function (key) {
        var input = form.querySelector('[data-aimi-lead-hidden="' + key + '"]');
        if (input && 'value' in input) {
          input.value = hiddenFields[key] || '';
        }
      });

      var subject = form.querySelector('[data-aimi-lead-subject]');
      if (subject && 'value' in subject) {
        subject.value = this.active.mode === 'stock'
          ? 'AIMI Stock Notification - ' + (context.productTitle || 'Product')
          : 'AIMI Quote Request - ' + (context.productTitle || 'Custom Request');
      }

      var shoeStyle = form.querySelector('[data-aimi-lead-field="shoe_style"]');
      if (shoeStyle && 'value' in shoeStyle && !shoeStyle.value && context.productTitle) {
        shoeStyle.value = context.productTitle;
      }

      var size = form.querySelector('[data-aimi-lead-field="size"]');
      var formattedSize = formatEuSize(context.selectedSize);
      if (size && formattedSize && !size.value) {
        size.value = formattedSize;
      }
    }

    getContext() {
      var product = this.productData.product || {};
      var variant = this.getCurrentVariant();
      var variantId = variant && variant.id ? String(variant.id) : this.getVariantIdFromForm();
      var colorPosition = Number(this.productData.colorOptionPosition || this.dataset.colorOptionPosition || 0);
      var sizePosition = Number(this.productData.sizeOptionPosition || this.dataset.sizeOptionPosition || 0);
      var selectedColor = colorPosition && variant ? variant['option' + colorPosition] || '' : '';
      var selectedSize = sizePosition && variant ? variant['option' + sizePosition] || '' : '';
      var productUrl = normalizeAbsoluteUrl(product.url || this.dataset.productUrl || window.location.pathname, variantId);

      return {
        productTitle: product.title || this.dataset.productTitle || '',
        handle: product.handle || this.dataset.productHandle || '',
        productUrl: productUrl,
        variantId: variantId || '',
        variantTitle: variant && variant.title ? variant.title : '',
        selectedColor: selectedColor || '',
        selectedSize: selectedSize || '',
        timestamp: new Date().toISOString()
      };
    }

    getCurrentVariant() {
      var variantId = this.getVariantIdFromForm();
      if (!variantId) variantId = this.selectedVariantId;
      if (!variantId && this.productData.selectedVariantId) variantId = String(this.productData.selectedVariantId);

      var variants = this.productData.variants || [];
      var variant = variants.find(function (item) {
        return String(item.id) === String(variantId);
      });

      return variant || variants[0] || null;
    }

    getVariantIdFromForm() {
      var root = this.closest('.shopify-section') || document;
      var productId = this.dataset.productId;
      var selector = productId
        ? 'product-form-component[data-product-id="' + cssEscape(productId) + '"] input[name="id"]'
        : 'product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]';
      var input = root.querySelector(selector);
      var value = input && 'value' in input ? String(input.value || '') : '';

      if (value) {
        this.selectedVariantId = value;
        this.dataset.selectedVariantId = value;
      }

      return value;
    }

    updateBodyField() {
      if (!this.active || !this.active.bodyField || !this.active.form) return;

      var form = this.active.form;
      var context = this.getContext();
      var value = function (key) {
        var field = form.querySelector('[data-aimi-lead-field="' + key + '"]');
        return fieldValue(field);
      };

      var lines;

      if (this.active.mode === 'stock') {
        lines = [
          'AIMI STOCK NOTIFICATION REQUEST',
          '',
          'Email:',
          value('email'),
          '',
          'Shoe Size:',
          value('size'),
          '',
          'Product:',
          context.productTitle,
          '',
          'Variant:',
          context.variantTitle,
          '',
          'Variant ID:',
          context.variantId,
          '',
          'Handle:',
          context.handle,
          '',
          'Product URL:',
          context.productUrl,
          '',
          'Selected Color:',
          context.selectedColor,
          '',
          'Submitted:',
          context.timestamp
        ];
      } else {
        lines = [
          'AIMI QUOTE REQUEST',
          '',
          'Name:',
          value('name'),
          '',
          'Email:',
          value('email'),
          '',
          'Phone:',
          value('phone'),
          '',
          'Shoe Style:',
          value('shoe_style'),
          '',
          'Shoe Size:',
          value('size'),
          '',
          'Reference Image:',
          value('reference_image'),
          '',
          'Notes:',
          value('notes'),
          '',
          'Product:',
          context.productTitle,
          '',
          'Variant:',
          context.variantTitle,
          '',
          'Variant ID:',
          context.variantId,
          '',
          'Handle:',
          context.handle,
          '',
          'Product URL:',
          context.productUrl,
          '',
          'Selected Color:',
          context.selectedColor,
          '',
          'Submitted:',
          context.timestamp
        ];
      }

      this.active.bodyField.value = lines.join('\n');
    }

    prepareCustomerForm() {
      if (!this.active || !this.active.customerForm || !this.active.form) return;

      var email = fieldValue(this.active.form.querySelector('[data-aimi-lead-field="email"]'));
      var context = this.getContext();
      var emailInput = this.active.customerForm.querySelector('[data-aimi-lead-customer-email]');
      var tagsInput = this.active.customerForm.querySelector('[data-aimi-lead-customer-tags]');
      var tags = tagsInput && 'value' in tagsInput ? tagsInput.value : 'aimi-lead';
      var additions = [
        this.active.mode === 'stock' ? 'aimi-stock-notify' : 'aimi-quote',
        context.handle ? 'product-' + sanitizeTag(context.handle) : '',
        context.variantId ? 'variant-' + sanitizeTag(context.variantId) : ''
      ].filter(Boolean);

      if (emailInput && 'value' in emailInput) {
        emailInput.value = email;
      }

      if (tagsInput && 'value' in tagsInput) {
        tagsInput.value = [tags].concat(additions).join(',');
      }
    }

    setSubmitting(isSubmitting) {
      if (!this.active || !this.active.submitButton) return;

      this.active.isSubmitting = isSubmitting;
      this.active.dialog.classList.toggle('is-submitting', isSubmitting);

      var button = this.active.submitButton;
      if (!button.dataset.aimiLeadOriginalLabel) {
        button.dataset.aimiLeadOriginalLabel = button.textContent.trim();
      }

      button.disabled = isSubmitting;
      button.setAttribute('aria-disabled', isSubmitting ? 'true' : 'false');
      button.textContent = isSubmitting
        ? button.getAttribute('data-aimi-lead-sending-label') || 'Sending'
        : button.dataset.aimiLeadOriginalLabel || button.getAttribute('data-aimi-lead-default-label') || 'Submit';
    }

    showSuccess() {
      if (!this.active) return;

      if (this.active.form) {
        this.active.form.hidden = true;
      }

      this.hideSystemError();

      if (this.active.successState) {
        this.active.successState.hidden = false;
        this.active.successState.focus();
      }

      this.successTimer = window.setTimeout(function () {
        this.close();
      }.bind(this), SUCCESS_DELAY);
    }

    showSystemError() {
      if (this.active && this.active.systemError) {
        this.active.systemError.hidden = false;
      }
    }

    hideSystemError() {
      if (this.active && this.active.systemError) {
        this.active.systemError.hidden = true;
      }
    }

    resetActiveState(resetForm) {
      if (!this.active) return;

      this.setSubmitting(false);
      this.hideSystemError();

      if (this.active.successState) {
        this.active.successState.hidden = true;
      }

      if (this.active.form) {
        this.active.form.hidden = false;

        if (resetForm) {
          this.active.form.reset();
        }

        this.active.form.querySelectorAll('[data-aimi-lead-field]').forEach(function (field) {
          this.setFieldError(field, '');
        }, this);
      }
    }

    close() {
      if (!this.active || !this.active.dialog || this.active.dialog.classList.contains('is-closing')) return;

      this.clearTimers();
      var active = this.active;
      active.dialog.classList.add('is-closing');

      this.closeTimer = window.setTimeout(function () {
        if (!active.dialog) return;

        if (typeof active.dialog.close === 'function' && active.dialog.open) {
          active.dialog.close();
        } else {
          active.dialog.removeAttribute('open');
        }

        this.unlockScroll();

        if (active.lastActiveElement && document.contains(active.lastActiveElement) && typeof active.lastActiveElement.focus === 'function') {
          active.lastActiveElement.focus();
        }

        this.removeActiveDialog(active);
      }.bind(this), CLOSE_DELAY);
    }

    removeActiveDialog(activeDialog) {
      var active = activeDialog || this.active;
      if (!active) return;

      if (active.abortController) {
        active.abortController.abort();
      }

      if (active.dialog && active.dialog.parentNode) {
        active.dialog.parentNode.removeChild(active.dialog);
      }

      if (!activeDialog || activeDialog === this.active) {
        this.active = null;
      }
    }

    getFocusableElements() {
      if (!this.active || !this.active.dialog) return [];

      return Array.prototype.slice.call(this.active.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (element) {
        return element.offsetParent !== null || element === document.activeElement;
      });
    }

    focusInitialField() {
      if (!this.active) return;

      var preferred = this.active.form && this.active.form.querySelector('[data-aimi-lead-field]:not([type="hidden"])');
      if (preferred && typeof preferred.focus === 'function') {
        preferred.focus();
        return;
      }

      var focusable = this.getFocusableElements();
      if (focusable.length) {
        focusable[0].focus();
      }
    }

    lockScroll() {
      var html = document.documentElement;
      this.hadScrollLock = html.hasAttribute('scroll-lock');
      this.isScrollLocked = true;
      html.setAttribute('scroll-lock', '');
    }

    unlockScroll() {
      if (!this.isScrollLocked) return;

      if (!this.hadScrollLock) {
        document.documentElement.removeAttribute('scroll-lock');
      }

      this.isScrollLocked = false;
    }

    clearTimers() {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      if (this.successTimer) {
        window.clearTimeout(this.successTimer);
        this.successTimer = null;
      }
    }
  }

  class AimiCustomQuoteAliasElement extends AimiLeadCaptureElement {}

  function handleGlobalLeadTrigger(event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var trigger = target.closest('[data-aimi-lead-capture-open], [data-aimi-custom-quote-open]');
    if (!trigger) return;

    var mode = trigger.getAttribute('data-aimi-lead-capture-open') || 'quote';
    var instance = closestLeadCaptureForTrigger(trigger);
    if (!instance || typeof instance.open !== 'function') return;

    event.preventDefault();
    instance.open(mode, trigger);
  }

  if ('customElements' in window) {
    if (!window.customElements.get('aimi-lead-capture')) {
      window.customElements.define('aimi-lead-capture', AimiLeadCaptureElement);
    }

    if (!window.customElements.get('aimi-custom-quote')) {
      window.customElements.define('aimi-custom-quote', AimiCustomQuoteAliasElement);
    }
  }

  document.addEventListener('click', handleGlobalLeadTrigger);

  window.AimiLeadCapture = {
    loaded: true,
    openNearest: function (trigger, mode) {
      var instance = closestLeadCaptureForTrigger(trigger);
      if (instance && typeof instance.open === 'function') {
        instance.open(mode || 'quote', trigger);
      }
    }
  };

  window.AimiCustomQuote = window.AimiLeadCapture;
})();
